import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FetchPolicy, gql } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { Equipment, EquipmentInput } from '@app/core/model/entities/equipments/equipment';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, from, Observable, Subject, switchMap } from 'rxjs';
import { map, reduce, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class EquipmentsService {
  private activationEndService = inject(ActivationEndService);
  private appManager = inject(AppManager);
  private assetsService = inject(AssetsService);
  private apiService = inject(ApiService);
  private router = inject(Router);
  private appConfig = inject(AppConfig);

  private sidePanelToggleSubject = new Subject<Equipment | null>();

  private addEquipmentSubject = new Subject<Equipment>();
  private updateEquipmentSubject = new Subject<Equipment>();
  private deleteEquipmentsSubject = new Subject<Equipment[]>();

  private readonly equipmentInfoGraphqlFragment = gql`
    fragment EquipmentInfo on Equipment {
      id
      identifier
      name
      organizationId
      assetId
      spaceIdsList
      equipmentIdsList
      properties
      computedProperties
      creationActor {
        actorId
        name
      }
      creationDate
      lastChangeActor {
        actorId
        name
      }
      lastChangeDate
      dataDate
    }
  `;

  /**
   * Emits every new Equipment after it was created.
   */
  public get equipmentAdded$(): Observable<Equipment> {
    return this.addEquipmentSubject.asObservable();
  }

  /**
   * Emits an Equipment whenever it has been updated.
   */
  public get equipmentUpdated$(): Observable<Equipment> {
    return this.updateEquipmentSubject.asObservable();
  }

  /**
   * Emits Equipments whenever they have been deleted.
   */
  public get equipmentsDeleted$(): Observable<Equipment[]> {
    return this.deleteEquipmentsSubject.asObservable();
  }

  /**
   * Emits data for the side panel to display or null whenever the side panel is closed.
   */
  public get sidePanelToggle$(): Observable<Equipment | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Loads equipments page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch equipments based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the equipments.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Equipment objects, each with additional user information.
   */
  public loadEquipmentsPage(selectParams: SelectParams): Observable<Equipment[]> {
    const query = gql`
      query Equipments($organizationId: String!, $selectParams: SelectParams!) {
        equipments(organizationId: $organizationId, selectParams: $selectParams) {
          ...EquipmentInfo
        }
      }
      ${this.equipmentInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Equipment, data['equipments'] as Equipment[])));
  }

  /**
   * Loads all equipment items pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch equipments based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the equipment items.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Equipment objects, each with additional user information.
   */
  public loadAllEquipments(selectParams: SelectParams): Observable<Equipment[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadEquipmentsPage(params)
      .pipe(
        expand((equipments, i) => {
          if (equipments.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadEquipmentsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Equipment[])
      );
  }

  /**
   * Fetch an Equipment by its ID.
   * @param equipmentId The ID of the equipment.
   * @param fetchPolicy GraphQL query fetch policy, default is 'network-only'.
   * @return Observable emitting the Equipment.
   */
  public loadEquipment(equipmentId: string, fetchPolicy: FetchPolicy = 'network-only'): Observable<Equipment> {
    const query = gql`
      query Equipment($id: String!) {
        equipment(id: $id) {
          ...EquipmentInfo
        }
      }
      ${this.equipmentInfoGraphqlFragment}
    `;
    const variables = {
      id: equipmentId
    };

    return this.apiService.query({query, variables, fetchPolicy})
      .pipe(
        map(data => plainToInstance(Equipment, data['equipment'])),
        tap(() => {
          if (fetchPolicy === 'cache-first') {
            this.activationEndService.getRefreshHeaderSubject().next();
          }
        })
      );
  }

  /**
   * Make an API request to create new Equipment with the provided data.
   * @param equipmentInput Equipment data.
   * @return Observable emitting the new Equipment that was created.
   */
  public createEquipment(equipmentInput: EquipmentInput): Observable<Equipment> {
    const mutation = gql`
      mutation CreateEquipment($equipmentInput: CreateEquipmentInput!) {
        createEquipment(equipmentInput: $equipmentInput) {
          ...EquipmentInfo
        }
      }
      ${this.equipmentInfoGraphqlFragment}
    `;
    const variables = {equipmentInput};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Equipment, data['createEquipment'])),
        tap(newEquipment => this.addEquipmentSubject.next(newEquipment))
      );
  }

  /**
   * Make an API request to copy the Equipments into another Asset.
   * @param equipments Equipments to duplicate.
   * @param assetId ID of Asset to copy the Equipment into.
   * @param spaceIds IDs of Spaces to link to new Equipment.
   * @return Observable emitting the Equipment that was created.
   */
  public duplicateEquipments(equipments: Equipment[], assetId: string, spaceIds: string[]): Observable<Equipment[]> {
    const mutation = gql`
      mutation DuplicateEquipment($equipmentIds: [String!]!, $assetId: String!, $spaceIds: [String!]!) {
        duplicateEquipments(ids: $equipmentIds, assetId: $assetId, spaceIds: $spaceIds) {
          ...EquipmentInfo
        }
      }${this.equipmentInfoGraphqlFragment}
    `;
    const variables = {
      equipmentIds: equipments.map(equipment => equipment.id),
      assetId,
      spaceIds
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Equipment, data['duplicateEquipments'] as Equipment[])),
        tap(newEquipment => newEquipment.forEach(duplicatedEquipment => this.addEquipmentSubject.next(
          duplicatedEquipment)))
      );
  }

  /**
   * Call the API to update Equipment.
   * @param equipment Equipment to update
   * @param equipmentInput Data to update the Equipment with.
   * @return Observable emitting the updated Equipment once the update is completed
   */
  public updateEquipment(equipment: Equipment, equipmentInput: EquipmentInput): Observable<Equipment> {
    const mutation = gql`
      mutation UpdateEquipment($updateEquipmentInput: UpdateEquipmentInput!) {
        updateEquipment(equipmentInput: $updateEquipmentInput) {
          ...EquipmentInfo
        }
      }
      ${this.equipmentInfoGraphqlFragment}
    `;
    const variables = {
      updateEquipmentInput: {
        id: equipment.id,
        name: equipment.name,
        spaceIds: equipment.spaceIds,
        equipmentIds: equipment.equipmentIds,
        assetId: equipment.assetId,
        ...equipmentInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Equipment, data['updateEquipment'])),
        tap(updatedEquipment => this.updateEquipmentSubject.next(updatedEquipment))
      );
  }

  /**
   * Call the API to update a list of Equipments.
   * @param equipments Equipments to update
   * @return Observable emitting the updated Equipments once the update is completed
   */
  public updateEquipments(equipments: {
    equipment: Equipment,
    equipmentInput: EquipmentInput
  }[]): Observable<Equipment[]> {
    const mutation = gql`
      mutation UpdateEquipments($equipmentInputs: [UpdateEquipmentInput!]!) {
        updateEquipments(equipmentInputs: $equipmentInputs) {
          ...EquipmentInfo
        }
      }
      ${this.equipmentInfoGraphqlFragment}
    `;
    const variables = {
      equipmentInputs: equipments.map(({equipment: {id, name, assetId, spaceIds, equipmentIds}, equipmentInput}) => {
        return {id, name, spaceIds, equipmentIds, assetId, ...equipmentInput};
      })
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Equipment, data['updateEquipments'] as Equipment[])),
        switchMap(equipments => from(equipments)),
        tap(updatedEquipment => this.updateEquipmentSubject.next(updatedEquipment)),
        toArray()
      );
  }

  /**
   * Make an API request to delete Equipments.
   * @param equipments Equipments to delete.
   * @return True if all Equipments have been deleted successfully, false otherwise.
   */
  public deleteEquipments(equipments: Equipment[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteEquipment($equipmentIds: [String!]!) {
        deleteEquipments(ids: $equipmentIds)
      }
    `;
    const variables = {equipmentIds: equipments.map(equipment => equipment.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteEquipments'] as boolean),
        tap(success => success && this.deleteEquipmentsSubject.next(equipments))
      );
  }

  /**
   * Open the sidenav with information about the selected Equipment.
   * @param equipment Equipment to be displayed in the side panel.
   */
  public openEquipmentSidePanel(equipment: Equipment): void {
    this.sidePanelToggleSubject.next(equipment);
  }

  /**
   * Close the sidenav.
   */
  public closeEquipmentSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }

  /**
   * Navigate to an Equipment's sheet.
   * @param equipmentId ID of the Equipment to navigate to.
   * @return Router's promise
   */
  public async navigateToEquipmentSheet(equipmentId: string): Promise<void> {
    await this.router.navigate([
      'organization',
      this.appManager.currentOrganization.id,
      'equipments',
      'equipment-sheet',
      equipmentId
    ]);
  }

  /**
   * Navigate to an Asset's sheet's equipments tab.
   * @param assetId ID of the Asset to navigate to.
   * @return Router's promise
   */
  public async navigateToAssetSheet(assetId: string): Promise<void> {
    await this.assetsService.navigateToAssetSheet(assetId, 'equipments');
  }
}
